import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Time, timeUpdateSchema } from "../../../shared/types/time";
import { Button } from "../components/button";
import { Modal } from "../components/modal";
import { TimeForm } from "../components/time/form";
import { FETCH_ERROR } from "../consts/fetch-error";
import { useConfirm } from "../providers/confirm";
import { emit } from "../services/event-listner";
import {
  deleteTime as deleteTimeService,
  getTime,
  updateTime,
} from "../services/time";
import { useLocale } from "../stores/locale";
import { dateFormat } from "../utils/date-format";
import { firstSecondOfMinute } from "../utils/first-second-of-minute";
import { isSameDay } from "../utils/is-same-day";
import { timeFormat } from "../utils/time-format";
import { getWeek } from "../utils/week";

export function TimeEdit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState<Time | null>();

  const dateParam = searchParams.get("date");

  if (!id) {
    throw new Error("Invalid id");
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setTime(await getTime(id));
      setLoading(false);
    })();
  }, [id]);

  function onHide() {
    navigate(`/time?date=${dateParam}`);
  }

  return (
    <Modal onClose={onHide}>
      {loading || !time ? (
        <div className="flex justify-center items-center h-full">
          Loading...
        </div>
      ) : (
        <Form id={id} time={time} onHide={onHide} />
      )}
    </Modal>
  );
}

function Form({
  id,
  time,
  onHide,
}: {
  id: string;
  time: Time;
  onHide: () => void;
}) {
  const [isValid, setIsValid] = useState(false);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const t = useLocale((state) => state.locale);

  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  function onChange(timeForm: TimeForm) {
    // Not valid if project or task is empty or toTime is set but not fromTime or toTime is not set but original toTime is set
    setIsValid(
      timeForm.project.trim() !== "" &&
        timeForm.task.trim() !== "" &&
        (!timeForm.endTime.trim() || timeForm.startTime.trim() !== "") &&
        (!time.to || timeForm.startTime.trim() !== ""),
    );
  }

  function onSubmit(timeForm: TimeForm) {
    (async () => {
      setSaving(true);
      const now = firstSecondOfMinute(new Date());

      const fromTime =
        timeForm.startTime.trim() === ""
          ? now
          : new Date(`${dateFormat(time.from)} ${timeForm.startTime}`);

      const toTime = timeForm.endTime.trim()
        ? new Date(`${dateFormat(time.from)} ${timeForm.endTime}`)
        : undefined;

      if (fromTime > now && !toTime) {
        // Set fromTime to the previous day if it is before now
        fromTime.setDate(fromTime.getDate() - 1);
      }

      if (toTime && toTime < fromTime) {
        // Set toTime to the next day if it is before fromTime
        toTime.setDate(toTime.getDate() + 1);
      }

      if (!isSameDay(fromTime, time.from)) {
        if (
          !(await confirm({
            message:
              "Start time is not on the same day as the original time. Are you sure you want to proceed?",
            header: "Confirmation",
          }))
        ) {
          setSaving(false);
          return;
        }
      }

      let updatedTime: Time;
      try {
        updatedTime = await updateTime(
          id,
          timeUpdateSchema.parse({
            ...(timeForm.project.trim()
              ? { project: timeForm.project.trim() }
              : {}),
            ...(timeForm.task.trim() ? { task: timeForm.task.trim() } : {}),
            ...(timeForm.description.trim()
              ? { description: timeForm.description.trim() }
              : {}),
            from: fromTime,
            ...(toTime ? { to: toTime } : {}),
          }),
        );
      } catch (error) {
        if (error === FETCH_ERROR.UNAUTHORIZED) {
          emit("error", error);
          return;
        }
        throw error;
      } finally {
        setSaving(false);
      }

      const originalDate = new Date(dateFormat(time.from));
      const updatedDate = new Date(dateFormat(updatedTime.from));
      const refreshDates = [originalDate];
      if (dateFormat(originalDate) !== dateFormat(updatedDate)) {
        refreshDates.push(updatedDate);
      }

      for (const date of refreshDates) {
        const weekDates = getWeek(date);
        await queryClient.refetchQueries({
          queryKey: ["get-times", date],
        });
        await queryClient.refetchQueries({
          queryKey: ["get-times", weekDates[0], weekDates[6]],
        });
      }

      onHide();
    })();
  }

  function deleteTime(id: string) {
    (async () => {
      if (
        !(await confirm({
          message: t.time.confirmDelete,
          header: t.confirmation,
        }))
      ) {
        return;
      }
      setDeleting(true);
      try {
        await deleteTimeService(id);
      } catch (error) {
        if (error === FETCH_ERROR.UNAUTHORIZED) {
          emit("error", error);
          return;
        }
        setDeleting(false);
        throw error;
      }

      const date = new Date(dateFormat(time.from));
      const weekDates = getWeek(date);

      // Invalidate time for date
      await queryClient.refetchQueries({
        queryKey: ["get-times", date],
      });

      // Invalidate time for week
      await queryClient.refetchQueries({
        queryKey: ["get-times", weekDates[0], weekDates[6]],
      });

      onHide();
    })();
  }

  return (
    <TimeForm
      extraButtons={
        <Button
          outline
          disabled={deleting || saving}
          loading={deleting}
          severity="danger"
          type="button"
          onClick={() => deleteTime(id)}
        >
          {t.time.delete}
        </Button>
      }
      initChange={true}
      isValid={isValid}
      loading={deleting}
      submitting={saving}
      value={
        time
          ? {
              ...time,
              startTime: timeFormat(time.from),
              endTime: time.to ? timeFormat(time.to) : "",
              description: time.description ?? "",
            }
          : undefined
      }
      onCancel={onHide}
      onChange={onChange}
      onSubmit={onSubmit}
    />
  );
}
